import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { finalize, Subject, takeUntil } from 'rxjs'
import { FoodItemModel } from 'src/app/models/food-item.model'
import { AyBService } from 'src/app/services/api/ayb.service'
import Swal from 'sweetalert2'
import * as XLSX from 'xlsx'
import { DialogSkusComponent } from './dialog-skus/dialog-skus.component'

interface ExcelRowData {
  codigo: string | undefined
  artCodigo: string | undefined
  uMedida: string | undefined
  artNombre: string | undefined
  cant: number // 0 if empty
}

@Component({
  selector: 'app-skus',
  templateUrl: './skus.component.html',
  styleUrls: ['./skus.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SkusComponent implements OnInit, OnDestroy {
  private _unsubscribeOnDestroy$ = new Subject<boolean>()
  foodItemsSku: FoodItemModel[] = []
  selectedCategories: string | null = null
  arrayCategorias: string[] = []
  activeTextArea = -1
  newStockRealInputValue = 0
  displayedColumns = [
    'id',
    'nombreInsumo',
    'categoria',
    'peso',
    'unidadMedida',
    'historialCostos',
    // 'stock',
    'stockTeorico',
    'stockAjuste',
    'stock',
    'estado',
    'modificar',
    'eliminar'
  ]

  dataSource = new MatTableDataSource<any>()
  isLoading: boolean = true

  paginator!: MatPaginator
  @ViewChild(MatPaginator) set matPaginator (paginator: MatPaginator) {
    this.paginator = paginator
    this.dataSource.paginator = paginator
  }
  sort!: MatSort
  @ViewChild(MatSort) set matSort (sort: MatSort) {
    this.sort = sort
    this.dataSource.sort = sort
  }

  constructor (
    public matDialog: MatDialog,
    private readonly cdRef: ChangeDetectorRef,
    private readonly alimentosService: AyBService
  ) {}

  ngOnInit (): void {
    this.getAllInsumos()
    this.dataSource = new MatTableDataSource()
  }
  ngOnDestroy (): void {
    this._unsubscribeOnDestroy$.next(true)
    this._unsubscribeOnDestroy$.complete()
  }
  getAllInsumos () {
    this.alimentosService
      .queryGetAllFoodItemsSku()
      .pipe(
        takeUntil(this._unsubscribeOnDestroy$),
        finalize(() => {
          this.isLoading = false
          this.cdRef.markForCheck()
        })
      )
      .subscribe({
        next: ({ getAllFoodItemsSku }) => {
          console.log({
            getAllFoodItemsSku
          })
          this.foodItemsSku = getAllFoodItemsSku?.insumos || []
          this.foodItemsSku.sort((a: any, b: any) =>
            a.nombreInsumo.localeCompare(b.nombreInsumo)
          )
          const categoria = this.foodItemsSku.map((item: any) => item.categoria)
          this.arrayCategorias = [...new Set(categoria)]
          this.dataSource.data = this.foodItemsSku
          this.dataSource.paginator = this.paginator
          this.dataSource.sort = this.sort
        },
        error: error => {
          console.error('Error fetching SKUs:', error)
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudieron cargar los SKUs. Inténtalo de nuevo antes de contactar al administrador.'
          })
        }
      })
  }

  applyFilter (event: Event) {
    const filterValue = (event.target as HTMLInputElement).value
    this.dataSource.filter = filterValue.trim().toLowerCase()
  }

  openDialog (foodItemSku: FoodItemModel | null = null): void {
    let dialogConfig = new MatDialogConfig()

    if (foodItemSku == null) {
      dialogConfig.data = {
        title: 'Agregar SKU',
        nombreInsumo: '',
        categoria: '',
        peso: '',
        unidadMedida: '',
        estadoSKU: '',
        listaSKU: this.foodItemsSku,
        estado: 2
      }
    } else {
      dialogConfig.data = {
        title: 'Modificar SKU',
        id: foodItemSku.id,
        codigoInsumo: foodItemSku.codigoInsumo,
        nombreInsumo: foodItemSku.nombreInsumo,
        categoria: foodItemSku.categoria,
        peso: foodItemSku.peso,
        unidadMedida: foodItemSku.unidadMedida,
        estadoSKU: foodItemSku.estado,
        listaSKU: this.foodItemsSku,
        stockReal: foodItemSku.stock,
        stockTeorico: foodItemSku.stockTeorico,
        estado: 1
      }
    }

    dialogConfig.width = '30rem'
    dialogConfig.height = '30rem'

    let dialogRef = this.matDialog.open(DialogSkusComponent, dialogConfig)

    dialogRef.afterClosed().subscribe(result => {
      if (result == 1) {
        this.getAllInsumos()
      }
    })
  }

  deleteSKU (element: FoodItemModel): void {
    Swal.fire({
      title: '¿Estás seguro de que quieres borrar este registro?',
      showCancelButton: true,
      confirmButtonText: 'Sí',
      confirmButtonColor: '#e95a1a',
      cancelButtonText: 'No'
    }).then(result => {
      if (result.isConfirmed) {
        this.alimentosService
          .mutationDeleteSKU(element?.id ?? '')
          .pipe(takeUntil(this._unsubscribeOnDestroy$))
          .subscribe({
            next: data => {
              console.log(data)
              this.getAllInsumos()
            },
            error: error => {
              console.error(error)
            }
          })
        Swal.fire({
          position: 'top-end',
          icon: 'success',
          title: 'El registro se eliminó satisfactoriamente.',
          showConfirmButton: false,
          timer: 4000
        })
      }
    })
  }

  applyFilterSelect () {
    let filteredFoodItemsSku = this.foodItemsSku

    if (this.selectedCategories) {
      filteredFoodItemsSku = filteredFoodItemsSku.filter(
        item => item?.categoria == this?.selectedCategories
      )
    }
    this.dataSource.data = filteredFoodItemsSku
  }

  setActiveInput (i: number, foodItemSku: FoodItemModel) {
    this.newStockRealInputValue = foodItemSku.stock || 0
    this.activeTextArea = i
    this.cdRef.markForCheck()
  }

  updateStockReal (foodItemSkuToUpdate: FoodItemModel) {
    this.activeTextArea = -1

    if (foodItemSkuToUpdate.stock === this.newStockRealInputValue) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        title: 'Misma cantidad. No se realizaron cambios.',
        icon: 'info',
        showConfirmButton: false,
        timer: 3000
      })
      return
    }

    this.alimentosService
      .mutationUpdateInsumo({
        insumoId: foodItemSkuToUpdate.id || '',
        updatedInsumo: {
          id: foodItemSkuToUpdate.id,
          stock: this.newStockRealInputValue
        }
      })
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe({
        next: () => {
          const updatedIndex = this.dataSource.data.findIndex(
            warehouseFoodItemSku =>
              warehouseFoodItemSku.id === foodItemSkuToUpdate.id
          )
          if (updatedIndex !== -1) {
            this.dataSource.data[updatedIndex].stock =
              this.newStockRealInputValue
            this.cdRef.markForCheck()
          }
          Swal.fire({
            toast: true,
            position: 'top-end',
            title: 'Stock actualizado exitosamente',
            icon: 'success',
            showConfirmButton: false,
            timer: 3000
          })
          this.newStockRealInputValue = 0
        },
        error: (error: any) => {
          console.error('Error updating stock:', error)
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo actualizar el stock. Inténtalo de nuevo.'
          })
          this.newStockRealInputValue = 0
        }
      })
  }

  onFileSelected (event: Event): void {
    const input = event.target as HTMLInputElement
    if (!input.files || input.files.length === 0) {
      return
    }

    const file = input.files[0]
    const reader = new FileReader()

    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })

        // Step 1: Validate sheet exists
        const requiredSheetName = 'inventario-real-almacen'
        const foundSheet = workbook.SheetNames.find(
          name => name.trim().toLowerCase() === requiredSheetName.toLowerCase()
        )

        if (!foundSheet) {
          Swal.fire({
            icon: 'error',
            title: 'Error: Hoja no encontrada',
            html: `
              <div style="text-align: left;">
                <p>La hoja de cálculo debe llamarse: <b>"${requiredSheetName}"</b></p>
                <br>
                <p><b>Hojas encontradas:</b> ${workbook.SheetNames.join(
                  ', '
                )}</p>
              </div>
            `,
            width: '600px'
          })
          return
        }

        // Step 1: Read Excel data
        const worksheet = workbook.Sheets[foundSheet]
        const rawJsonData: any[][] = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: ''
        })

        // Find header row
        const headerResult = this._findHeaderRow(rawJsonData)
        if (headerResult.error) {
          Swal.fire({
            icon: 'error',
            title: 'Error: Encabezados no encontrados',
            text: headerResult.error,
            width: '600px'
          })
          return
        }

        // Extract data from header row onwards
        const jsonData = rawJsonData.slice(headerResult.headerRowIndex!)
        const validationError = this._validateExcelData(jsonData)
        if (validationError) {
          Swal.fire({
            icon: 'error',
            title: 'Error de validación',
            text: validationError,
            width: '600px'
          })
          return
        }

        const extractedData = this._extractExcelData(jsonData)
        console.log('Extracted data:', extractedData)

        // Step 2 & 3: Validate names and allow continuation
        this._validateAndProcessExcelData(extractedData)
      } catch (error) {
        console.error('Error reading Excel file:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo leer el archivo Excel. Verifica el formato.'
        })
      }
    }

    reader.onerror = () => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al leer el archivo.'
      })
    }

    reader.readAsArrayBuffer(file)
    input.value = ''
  }

  private _findHeaderRow (data: any[][]): {
    headerRowIndex?: number
    error?: string
  } {
    const expectedHeaders = [
      'CODIGO',
      'ART. CODIGO',
      'U. MEDIDA',
      'ART. NOMBRE',
      'CANT'
    ]

    // Search for the header row in the first 20 rows
    for (let rowIndex = 0; rowIndex < Math.min(data.length, 20); rowIndex++) {
      const row = data[rowIndex]
      if (!row) continue

      // Check if this row contains all expected headers
      let matchCount = 0
      for (let colIndex = 0; colIndex < row.length; colIndex++) {
        const cellValue = row[colIndex]?.toString().trim()
        if (expectedHeaders.includes(cellValue)) {
          matchCount++
        }
      }

      // If we found all 5 headers in this row, it's our header row
      if (matchCount === 5) {
        // Verify they are in the correct order (first 5 columns)
        let isCorrectOrder = true
        for (let i = 0; i < expectedHeaders.length; i++) {
          if (row[i]?.toString().trim() !== expectedHeaders[i]) {
            isCorrectOrder = false
            break
          }
        }

        if (isCorrectOrder) {
          return { headerRowIndex: rowIndex }
        } else {
          return {
            error: `Se encontraron los encabezados en la fila ${
              rowIndex + 1
            }, pero no están en el orden correcto. Orden esperado: ${expectedHeaders.join(
              ', '
            )}`
          }
        }
      }
    }

    return {
      error:
        'No se encontraron los encabezados requeridos (CODIGO, ART. CODIGO, U. MEDIDA, ART. NOMBRE, CANT) en las primeras 20 filas.'
    }
  }

  private _validateExcelData (jsonData: any[][]): string | null {
    if (!jsonData || jsonData.length === 0) {
      return 'El archivo está vacío.'
    }

    // Validate headers
    const headers = jsonData[0]
    const expectedHeaders = [
      'CODIGO',
      'ART. CODIGO',
      'U. MEDIDA',
      'ART. NOMBRE',
      'CANT'
    ]

    if (headers.length < 5) {
      return `Se esperan 5 columnas, pero se encontraron ${headers.length}. Formato requerido: CODIGO, ART. CODIGO, U. MEDIDA, ART. NOMBRE, CANT`
    }

    for (let i = 0; i < expectedHeaders.length; i++) {
      if (headers[i]?.toString().trim() !== expectedHeaders[i]) {
        return `Error en el encabezado de la columna ${i + 1}. Se esperaba "${
          expectedHeaders[i]
        }" pero se encontró "${headers[i] || '(vacío)'}".`
      }
    }

    // Validate data rows
    for (let rowIndex = 1; rowIndex < jsonData.length; rowIndex++) {
      const row = jsonData[rowIndex]
      const rowNumber = rowIndex + 1 // Excel row number (1-based + 1 for header)

      // Skip completely empty rows
      if (!row || row.every(cell => !cell && cell !== 0)) {
        continue
      }

      // Only validate col4: ART. NOMBRE (required string)
      // Other columns can be empty and will be stored as undefined
      if (!row[3] || row[3].toString().trim() === '') {
        return `Fila ${rowNumber}: La columna ART. NOMBRE está vacía. Este campo es obligatorio.`
      }

      // Validate col5: CANT (optional number, but must be valid if present)
      // Empty values are treated as 0
      if (row[4] !== '' && row[4] != null) {
        const cantValue = parseFloat(row[4])
        if (isNaN(cantValue)) {
          return `Fila ${rowNumber}: La columna CANT debe ser un número válido. Valor encontrado: "${row[4]}".`
        }
      }
      // If CANT is empty, it will be treated as 0 in extractExcelData
    }

    return null
  }

  private _extractExcelData (jsonData: any[][]): ExcelRowData[] {
    const extractedData: ExcelRowData[] = []

    // Skip header row (index 0), start from data rows
    for (let rowIndex = 1; rowIndex < jsonData.length; rowIndex++) {
      const row = jsonData[rowIndex]

      // Skip completely empty rows
      if (!row || row.every((cell: any) => !cell && cell !== 0)) {
        continue
      }

      // Extract data, storing empty cells as undefined
      const rowData: ExcelRowData = {
        codigo:
          row[0] && row[0].toString().trim() !== ''
            ? row[0].toString().trim()
            : undefined,
        artCodigo:
          row[1] && row[1].toString().trim() !== ''
            ? row[1].toString().trim()
            : undefined,
        uMedida:
          row[2] && row[2].toString().trim() !== ''
            ? row[2].toString().trim()
            : undefined,
        artNombre:
          row[3] && row[3].toString().trim() !== ''
            ? row[3].toString().trim()
            : undefined,
        cant: row[4] !== '' && row[4] != null ? parseFloat(row[4]) : 0 // Empty CANT = 0
      }

      extractedData.push(rowData)
    }

    return extractedData
  }

  private _validateAndProcessExcelData (excelData: ExcelRowData[]): void {
    // Step 4: Find matching insumos and categorize
    const notInWarehouseWithStock: ExcelRowData[] = []
    const matches: { skuId: string; newStock: number }[] = []

    for (const item of excelData) {
      if (!item.artNombre) continue

      // 4.1: Find warehouse SKU by matching trim() lowercase names (artNombre)
      const warehouseSku = this.foodItemsSku.find(
        sku =>
          sku.nombreInsumo &&
          sku.nombreInsumo.trim().toLowerCase() ===
            item.artNombre!.trim().toLowerCase()
      )

      if (!warehouseSku) {
        // 4.2: If insumo doesn't exist in warehouse and cant is 0 (empty cell), omit
        if (item.cant === 0) {
          continue // Skip this row
        }
        // 4.3: If insumo doesn't exist but has cant value, store it
        notInWarehouseWithStock.push(item)
      } else {
        // Match found - store for batch update
        matches.push({
          skuId: warehouseSku.id!,
          newStock: item.cant
        })
      }
    }

    // Display rows that don't exist in warehouse but have stock (4.3)
    if (notInWarehouseWithStock.length > 0) {
      const list = notInWarehouseWithStock
        .map(
          (item, i) =>
            `${i + 1}. ART. NOMBRE: "${item.artNombre}" - CANT: <b>${
              item.cant
            }</b>${item.artCodigo ? ` - ART. CODIGO: "${item.artCodigo}"` : ''}`
        )
        .join('<br>')

      Swal.fire({
        icon: 'warning',
        title: 'Insumos no encontrados en almacén',
        html: `
          <div style="text-align: left;">
            <p><b>❌ Los siguientes insumos tienen cantidad pero no existen en el almacén (${notInWarehouseWithStock.length}):</b></p>
            <div style="max-height: 300px; overflow-y: auto; padding: 10px; background-color: #ffe6e6; border-radius: 5px; margin-bottom: 15px;">
              ${list}
            </div>
            <p><b>✅ Coincidencias encontradas: ${matches.length}</b></p>
            <br>
            <p>¿Deseas continuar actualizando solo los insumos encontrados?</p>
          </div>
        `,
        width: '750px',
        showCancelButton: true,
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar'
      }).then(result => {
        if (result.isConfirmed) {
          this._updateStockBatch(matches)
        }
      })
    } else {
      // No warnings, proceed directly
      this._updateStockBatch(matches)
    }
  }

  private _updateStockBatch (
    matches: { skuId: string; newStock: number }[]
  ): void {
    if (matches.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Sin coincidencias',
        text: 'No se encontraron coincidencias exactas para actualizar.',
        timer: 3000
      })
      return
    }

    Swal.fire({
      title: 'Actualizando stocks...',
      html: `Actualizando <b>${matches.length}</b> insumo(s)...`,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    this.alimentosService
      .mutationUpdateSkuRealStocks(matches)
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'Actualización exitosa',
            html: `Se actualizaron <b>${matches.length}</b> insumo(s) correctamente.`,
            timer: 3000,
            showConfirmButton: false
          })
          this.getAllInsumos()
        },
        error: error => {
          console.error('Error updating stocks:', error)
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo actualizar los stocks. Inténtalo de nuevo.'
          })
        }
      })
  }
}
